<template>
  <div
    v-if="isSmall"
    class="tw:border tw:border-gray-300 tw:flex tw:flex-row tw:items-center tw:h-auto tw:gap-x-2 tw:p-3 tw:flex-wrap"
  >
    <p class="title tw:font-bold tw:inline tw:text-sm tw:items-start tw:gap-x-1" v-html="$t('rc_connection')" />

    <div
      v-if="currentPartner?.partnerStatus < 0"
      class="obsolete tw:flex tw:items-center tw:justify-start tw:px-2 tw:py-1 tw:rounded-lg tw:text-sm tw:bg-red-200 tw:text-red-700"
      v-html="$t('rc_provider_disconnected')"
    />
    <div
      v-else
      class="tw:flex tw:items-center tw:justify-start tw:px-2 tw:py-1 tw:rounded-lg tw:text-sm"
      :class="connectivityColors"
    >
      {{ currentPartner?.partnerName }}
      <span class="tw:font-bold">{{ `: ${$t(statusKeys[partnerStatus])}` }}</span>
    </div>
  </div>
  <Card
    v-else
    :title="$t('rc_connection')"
    :hint="
      currentPartner?.partnerStatus < 0
        ? $t(hint)
        : $t('/snippets/hgw/rc_settings_status_active_info.inc', {
            partnerName: currentPartner?.partnerName,
          })
    "
    :isActionable="selectablePartners?.length > 0 && isActionable"
    :isCustomTooltip="true"
    :partnerId="currentPartner?.partnerStatus < 0 ? 0 : currentPartner?.partnerId"
    :partnerName="currentPartner?.partnerName"
    type="connectivityProvider"
    :hasHint="hasHint"
  >
    <template #cardData>
      <div
        v-if="currentPartner?.partnerStatus < 0"
        class="obsolete tw:flex tw:items-center tw:justify-start tw:px-2 tw:py-1 tw:rounded tw:text-sm tw:bg-red-200 tw:text-red-700"
        v-html="$t('rc_provider_disconnected')"
      />
      <div
        v-else
        class="tw:flex tw:items-center tw:justify-start tw:px-2 tw:py-1 tw:rounded tw:text-sm"
        :class="connectivityColors"
      >
        {{ currentPartner?.partnerName }}
        <span class="tw:font-bold">{{ `: ${$t(statusKeys[partnerStatus])}` }}</span>
      </div>
    </template>
    <template #dropdownMenu="{ closeMenu }">
      <div
        class="listItem tw:px-4 tw:py-3 tw:text-sm tw:flex tw:flex-col tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:first:rounded-t-lg tw:last:rounded-b-lg"
        v-for="partner in orderedPartners"
        :key="partner?.partnerId"
        :class="currentPartnerClass(partner)"
        @click="onUpdatePartnerConfirm(partner, closeMenu)"
      >
        <p class="tw:flex tw:justify-between tw:items-center tw:w-full">
          {{ partner?.partnerName }}
          <span class="tw:text-xs" v-if="partner?.partnerStatus !== 5 || partner?.connectionStatus !== 5">{{
            $t('rc_provider_status_inactive')
          }}</span>
        </p>

        <div
          v-if="campaignType === 'cpc' && partner.canNetCpa"
          class="subtitle tw:flex tw:w-full tw:text-xs tw:flex tw:items-center tw:h-[24px]"
          :class="
            partner?.partnerId === currentPartner?.partnerId && isPartnerActive(partner)
              ? 'selected tw:text-blue-800'
              : 'tw:text-gray-600'
          "
        >
          <CheckMarkInCircle
            class="icon tw:fill-none tw:mr-1 tw:stroke-blue-800"
            :class="
              partner?.partnerId === currentPartner?.partnerId && isPartnerActive(partner) ? 'tw:flex' : 'tw:hidden'
            "
          />
          <p v-html="$t('rc_ncpa_supported')" />
        </div>
      </div>
    </template>
  </Card>
</template>
<script setup>
import { computed } from 'vue'
import Card from '@/components/rateConnect/statusBar/Card.vue'
import CheckMarkInCircle from '@/components/icons/CheckMarkInCircle.svg?component'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import appAnalytics from '@/utils/tracking'

import { usePartners, usePartnersMutation } from '@/components/rateConnect/queries'
import { selectedAccommodationId } from '@/layouts/queries'

const statusKeys = {
  active: 'rc_provider_status_active',
  inactive: 'rc_provider_status_inactive',
}
const { changePartner } = usePartnersMutation()
const { partnersData } = usePartners(selectedAccommodationId)
const { displayNotification, showSaveError } = useToastNotifications()
const props = defineProps({
  currentPartner: Object,
  partnerStatus: String,
  unavailabilityRate: [Number, String],
  campaignStatus: String,
  campaignType: String,
  budgetPercentage: [String, Number],
  isActionable: Boolean,
  hasHint: Boolean,
  isSmall: {
    type: Boolean,
    default: false,
  },
})

const selectablePartners = computed(() => {
  if (props.campaignType === 'netCpa') {
    return partnersData.value?.filter(
      item => item?.partnerId !== props.currentPartner?.partnerId && item.canNetCpa && item.partnerStatus >= 0
    )
  } else {
    return partnersData.value?.filter(
      item => item?.partnerId !== props.currentPartner?.partnerId && item?.partnerStatus >= 0
    )
  }
})

const orderedPartners = computed(() => {
  if (!selectablePartners.value) return []
  const selectablePartnersCopy = [...selectablePartners.value]

  const activePartners = selectablePartnersCopy.filter(item => item.connectionStatus === 5 && item.partnerStatus === 5)
  const inactivePartners = selectablePartnersCopy.filter(
    item => item.connectionStatus !== 5 || (item.partnerStatus >= 0 && item.partnerStatus !== 5)
  )

  let result = [...activePartners, ...inactivePartners]
  result = props.campaignType === 'netCpa' ? result.filter(item => item.canNetCpa) : result
  if (props.currentPartner && props.currentPartner.partnerStatus >= 0) {
    result.unshift(props.currentPartner)
  }

  return result
})

const hint = computed(() => {
  const activePartners = selectablePartners.value.filter(
    item => item.connectionStatus === 5 && item.partnerStatus === 5
  )
  if (activePartners.length > 0) {
    return 'rc_disconnected_alternative_tooltip'
  } else return 'rc_disconnected__no_alternative_tooltip'
})
const isPartnerActive = partner => {
  if (partner.partnerStatus === 5 && partner.connectionStatus === 5) return true
  else return false
}

const connectivityColors = computed(() => {
  if (props.partnerStatus !== 'active') return 'tw:bg-red-200 tw:text-red-700'
  else if (
    props.campaignStatus === 'PAUSED' ||
    props.campaignStatus === 'PAUSED_FOR_BUDGET' ||
    (Number(props.unavailabilityRate) === 100 && props.campaignType === 'netCpa') ||
    (props.campaignType === 'cpc' && Number(props.budgetPercentage) <= 0) ||
    (props.campaignType === 'cpc' && isNaN(props.budgetPercentage))
  )
    return 'tw:bg-gray-200 tw:text-gray-700'
  else if (props.partnerStatus === 'active') return 'tw:bg-green-200 tw:text-green-700'
  else return ''
})
const currentPartnerClass = partner => {
  if (partner?.partnerId === props.currentPartner?.partnerId) {
    return 'tw:bg-blue-200 tw:text-blue-800 tw:hover:cursor-default'
  } else if (!isPartnerActive(partner)) {
    return 'inactive tw:bg-gray-100 tw:text-gray-600 tw:hover:cursor-default tw:hover:bg-gray-100 tw:hover:text-gray-600'
  }
}

const onUpdatePartnerConfirm = (partner, closeMenu) => {
  if (props.currentPartner?.partnerId === partner?.partnerId) return
  if (!isPartnerActive(partner)) return

  appAnalytics.track(appAnalytics.events.RC_STATUS_BAR_MENU_OPTION_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    cardType: 'connectivityProvider',
    option: partner.partnerId,
    campaignType: 'netCpa',
  })

  closeMenu()
  changePartner(
    {
      itemId: selectedAccommodationId.value,
      partnerId: partner.partnerId,
    },
    {
      onSuccess: () => {
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_connectivity_provider_change_success',
          translationParams: { name: partner.partnerName },
          isTranslationKey: true,
          isHtml: true,
          type: 'success',
        })
      },
      onError: () => {
        showSaveError()
      },
    }
  )
}
</script>
<style scoped>
.listItem:hover > .subtitle p:deep(span),
.selected p:deep(span) {
  font-weight: bold;
}
.inactive:hover > .subtitle p:deep(span) {
  font-weight: normal;
}

.listItem:hover > .subtitle {
  color: var(--tw-color-blue-800);
}

.inactive:hover > .subtitle {
  color: var(--tw-color-gray-600);
}

.listItem:hover > .subtitle .icon {
  display: flex;
}
.inactive:hover > .subtitle .icon {
  display: none;
}
.obsolete:deep(span) {
  font-weight: bold;
  margin-left: 2px;
}
</style>
